import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Send, Paperclip, Download, MessageSquare } from 'lucide-react';
import { clientApi, formatDate, backendUrl, getClientToken } from '../../components/client-portal/clientPortalApi';

export default function ClientMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const reload = async () => {
    const items = await clientApi('/client/messages');
    setMessages(items);
    // Mark as read
    try { await clientApi('/client/messages/read', { method: 'PATCH' }); } catch (_) {}
  };

  useEffect(() => {
    let stop = false;
    (async () => { try { await reload(); } finally { if (!stop) setLoading(false); } })();
    const t = setInterval(() => { if (!stop) reload().catch(() => {}); }, 15_000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await clientApi('/client/messages', { method: 'POST', body: { body: draft } });
      setDraft('');
      await reload();
    } catch (e) { alert(e.message); } finally { setSending(false); }
  };

  const sendAttachment = async (file) => {
    if (!file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (draft.trim()) fd.append('body', draft);
      await clientApi('/client/messages/upload', { method: 'POST', formData: fd });
      setDraft('');
      await reload();
    } catch (e) { alert(e.message); } finally { setSending(false); }
  };

  const downloadAttachment = async (m) => {
    const token = getClientToken();
    const res = await fetch(`${backendUrl}/api/client/messages/${m.id}/attachment`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = m.attachment_filename || 'attachment';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="client-messages">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Messages</h1>
        <p className="text-slate-500 mt-1 text-sm">Chat directly with your broker. They'll be notified instantly.</p>
      </div>

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 flex flex-col h-[68vh]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-feed">
          {messages.length === 0 && (
            <div className="text-center text-slate-400 py-16">
              <MessageSquare className="w-10 h-10 mx-auto text-slate-300" />
              <div className="mt-2 text-sm">No messages yet — say hello to your broker.</div>
            </div>
          )}
          {messages.map((m) => {
            const mine = m.sender_type === 'client';
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ring-1 ${
                  mine ? 'bg-emerald-500 text-white ring-emerald-500' : 'bg-slate-50 text-slate-900 ring-slate-200'
                }`}>
                  {!mine && <div className="text-[11px] font-medium text-slate-500 mb-0.5">{m.sender_name}</div>}
                  {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
                  {m.has_attachment && (
                    <button
                      type="button"
                      onClick={() => downloadAttachment(m)}
                      className={`mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded ring-1 ${
                        mine ? 'bg-emerald-600/40 ring-emerald-300/40 hover:bg-emerald-600/60' : 'bg-white ring-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <Download className="w-3 h-3" />
                      <span>{m.attachment_filename}</span>
                    </button>
                  )}
                  <div className={`text-[10px] mt-1 ${mine ? 'text-emerald-50/80' : 'text-slate-400'}`}>
                    {formatDate(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-100 p-3">
          <div className="flex items-end gap-2">
            <input
              type="file"
              ref={fileRef}
              className="hidden"
              data-testid="message-file"
              onChange={(e) => sendAttachment(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="p-2.5 rounded-lg ring-1 ring-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50"
              data-testid="message-attach"
              aria-label="Attach file"
              disabled={sending}
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <textarea
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message…"
              data-testid="message-input"
              className="flex-1 px-3 py-2 rounded-lg ring-1 ring-slate-200 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
            />
            <button
              type="button"
              onClick={send}
              disabled={sending || !draft.trim()}
              data-testid="message-send"
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
